import log from 'loglevel'

/**
 * Enhanced logging service that provides a centralized interface for application logging.
 * Uses loglevel for better log level management and performance.
 */
export class Logger {
  private context: string

  private logger: log.Logger

  private timers: Map<string, number>

  constructor(context: string = 'App') {
    this.context = context
    this.logger = log.getLogger(context)
    this.timers = new Map()

    // Set default log level based on environment
    if (process.env.NODE_ENV === 'production') {
      this.logger.setLevel(log.levels.WARN)
    } else {
      this.logger.setLevel(log.levels.DEBUG)
    }
  }

  /**
   * Log informational messages
   */
  info(message: string, ...args: any[]): void {
    this.logger.info(`[${this.context}] ${message}`, ...args)
  }

  /**
   * Log error messages
   */
  error(message: string, ...args: any[]): void {
    this.logger.error(`[${this.context}] ${message}`, ...args)
  }

  /**
   * Log warning messages
   */
  warn(message: string, ...args: any[]): void {
    this.logger.warn(`[${this.context}] ${message}`, ...args)
  }

  /**
   * Log debug messages
   */
  debug(message: string, ...args: any[]): void {
    this.logger.debug(`[${this.context}] ${message}`, ...args)
  }

  /**
   * Log trace messages (most verbose level)
   */
  trace(message: string, ...args: any[]): void {
    this.logger.trace(`[${this.context}] ${message}`, ...args)
  }

  /**
   * Set the log level for this logger instance
   */
  setLevel(level: log.LogLevelDesc): void {
    this.logger.setLevel(level)
  }

  /**
   * Get the current log level
   */
  getLevel(): number {
    return this.logger.getLevel()
  }

  /**
   * Check if a specific level is enabled by comparing with current level
   */
  isLevelEnabled(level: log.LogLevelDesc): boolean {
    const currentLevel = this.logger.getLevel()
    const targetLevel = typeof level === 'string' ? log.levels[level.toUpperCase() as keyof log.LogLevel] : level
    return targetLevel !== undefined && currentLevel <= targetLevel
  }

  /**
   * Create a new logger instance with a specific context
   */
  withContext(context: string): Logger {
    return new Logger(context)
  }

  /**
   * Enable all log levels (useful for debugging)
   */
  enableAll(): void {
    this.logger.enableAll()
  }

  /**
   * Disable all log levels (useful for production)
   */
  disableAll(): void {
    this.logger.disableAll()
  }

  /**
   * Start timing an operation
   */
  time(label: string): void {
    this.timers.set(label, performance.now())
  }

  /**
   * End timing an operation and log the duration
   */
  timeEnd(label: string): void {
    const startTime = this.timers.get(label)
    if (startTime !== undefined) {
      const duration = performance.now() - startTime
      this.timers.delete(label)
      this.logger.debug(`${label}: ${duration.toFixed(2)}ms`)
    }
  }

  /**
   * Time an async operation and log the duration
   * @param label Description of the operation
   * @param operation The async operation to time
   * @returns The result of the operation
   */
  async timeAsync<T>(label: string, operation: () => Promise<T>): Promise<T> {
    const startTime = performance.now()
    try {
      const result = await operation()
      const duration = performance.now() - startTime
      this.logger.debug(`${label}: ${duration.toFixed(2)}ms`)
      return result
    } catch (error) {
      const duration = performance.now() - startTime
      this.logger.error(`${label} failed after ${duration.toFixed(2)}ms`, error)
      throw error
    }
  }
}

// Default logger instance
export const logger = new Logger()

// Convenience function to create context-specific loggers
export const createLogger = function (context: string): Logger {
  return new Logger(context)
}

// Export loglevel for direct access when needed
export { log }

// Export log levels for configuration
export const LogLevels = log.levels
